/**
 * Idaho State-Specific Diagnostic Questions
 * 
 * IMPORTANT REGULATORY NOTE:
 * Idaho is a MINIMAL REGULATION state for home care:
 * - Non-medical personal care agencies: NO STATE LICENSE REQUIRED
 * - Medicare-certified HHAs: NO STATE LICENSE REQUIRED as of July 1, 2025
 *   (per Streamlined Facilities Licensing and Inspection Act)
 * - IDAPA 16.03.07 (Home Health Agency Rules): REPEALED/NOT IN CURRENT CODE
 * 
 * What DOES apply:
 * - IDAPA 16.05.06: Criminal History and Background Checks
 * - General business registration (Secretary of State)
 * - Federal CMS requirements for Medicare-certified agencies (42 CFR Part 484)
 * - Idaho Medicaid provider enrollment for Medicaid billing
 * - Workers' compensation insurance (Idaho Industrial Commission)
 * 
 * Regulatory Body: Idaho Department of Health and Welfare (DHW)
 * Bureau of Facility Standards: (208) 334-6626
 * 
 * Last Verified: December 2024
 * Source: https://healthandwelfare.idaho.gov/providers/acute-and-continuing-care/home-health-agencies
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const IDAHO_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: BUSINESS REGISTRATION & OPERATIONS
  // ============================================
  {
    id: 'ID-A1',
    itemId: 'A1',
    stateCode: 'ID',
    title: 'Idaho Business Registration',
    description: 'Idaho requires general business registration but NOT a state home care license for non-medical personal care agencies',
    severity: 'high',
    regulationCode: 'Title 30',
    regulationName: 'Business Registration',
    agencyName: 'Idaho Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(208) 334-2301',
    agencyUrl: 'https://sos.idaho.gov/business/',
    questions: [
      {
        id: 'q1',
        question: 'Is your business entity registered with the Idaho Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'All businesses operating in Idaho must register with the Secretary of State (LLC, Corporation, etc.)',
      },
      {
        id: 'q2',
        question: 'What type of home care services do you provide?',
        inputType: 'select',
        options: [
          { value: 'non_medical', label: 'Non-medical personal care only (ADL assistance, companionship, homemaking)' },
          { value: 'medicare_hha', label: 'Medicare-certified Home Health Agency (skilled nursing, therapy)' },
          { value: 'both', label: 'Both non-medical and skilled services' },
        ],
        required: true,
        helpText: 'Idaho does NOT require a state license for either type. Medicare HHAs need federal CMS certification only.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your business is registered with the Idaho Secretary of State.',
      atRisk: 'Verify your business registration is current.',
      nonCompliant: 'Register your business with the Idaho Secretary of State at (208) 334-2301 or sos.idaho.gov.',
      unknown: 'Verify business registration status.',
    },
    stateNotes: [
      'IMPORTANT: Idaho does NOT require a state license for non-medical home care agencies',
      'Medicare-certified HHAs: State licensure eliminated July 1, 2025 per Streamlined Facilities Licensing and Inspection Act',
      'Only federal CMS certification required for Medicare billing',
      'Business registration (LLC/Corp) still required for all businesses',
    ],
  },

  {
    id: 'ID-A2',
    itemId: 'A2',
    stateCode: 'ID',
    title: 'Tax Registration',
    description: 'Idaho requires state tax registration for employers',
    severity: 'high',
    regulationCode: 'Title 63',
    regulationName: 'Tax Registration',
    agencyName: 'Idaho State Tax Commission',
    agencyAbbr: 'ISTC',
    agencyPhone: '(208) 334-7660',
    agencyUrl: 'https://tax.idaho.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are you registered with the Idaho State Tax Commission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a federal Employer Identification Number (EIN)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Tax registrations are in place.',
      atRisk: 'Verify all tax registrations are current.',
      nonCompliant: 'Register with the Idaho State Tax Commission at (208) 334-7660.',
      unknown: 'Verify tax registration status.',
    },
    stateNotes: [
      'All Idaho employers must register with the Idaho State Tax Commission',
      'Apply for EIN through IRS.gov or by phone at (800) 829-4933',
      'State withholding registration required for employee wages',
      'Online registration available at tax.idaho.gov',
    ],
  },

  {
    id: 'ID-A3',
    itemId: 'A3',
    stateCode: 'ID',
    title: 'Workers\' Compensation Insurance',
    description: 'Idaho requires workers\' compensation for employers with one or more employees',
    severity: 'high',
    regulationCode: '72-301',
    regulationName: 'Workers Compensation',
    agencyName: 'Idaho Industrial Commission',
    agencyAbbr: 'IIC',
    agencyPhone: '(208) 334-6000',
    agencyUrl: 'https://iic.idaho.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have employees (W-2 workers)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - independent contractors only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you maintain workers\' compensation insurance coverage?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Idaho requires workers\' compensation for all employers with one or more employees per Idaho Code 72-301.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Workers\' compensation requirements are met.',
      atRisk: 'Verify workers\' compensation coverage is adequate.',
      nonCompliant: 'Idaho requires workers\' compensation insurance per Idaho Code 72-301. Contact the Idaho Industrial Commission at (208) 334-6000.',
      unknown: 'Verify workers\' compensation requirements.',
    },
    stateNotes: [
      'Idaho Code 72-301 requires workers\' compensation for employers with one or more employees',
      'Coverage must be obtained through private insurance or state fund',
      'Idaho Industrial Commission: (208) 334-6000 or iic.idaho.gov',
      'Failure to maintain coverage is a misdemeanor with penalties up to $10,000',
      'Sole proprietors may exempt themselves but must notify the IIC',
    ],
  },

  {
    id: 'ID-A4',
    itemId: 'A4',
    stateCode: 'ID',
    title: 'General Liability Insurance',
    description: 'General liability insurance is recommended for home care agencies',
    severity: 'medium',
    regulationCode: 'Best Practice',
    regulationName: 'Insurance',
    agencyName: 'N/A - Business Best Practice',
    agencyAbbr: 'N/A',
    agencyPhone: '',
    agencyUrl: '',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain general liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'While not state-mandated for non-medical agencies, general liability insurance protects your business.',
      },
      {
        id: 'q2',
        question: 'Do you maintain professional liability insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Professional liability (E&O) insurance is recommended, especially if providing skilled services.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Liability insurance coverage is comprehensive.',
      atRisk: 'Consider adding professional liability insurance for additional protection.',
      nonCompliant: 'While not state-mandated, liability insurance is strongly recommended to protect your business.',
      unknown: 'Review insurance coverage with a qualified broker.',
    },
    stateNotes: [
      'General liability insurance is not state-mandated for non-medical agencies',
      'Industry standard: $1M per occurrence, $3M aggregate general liability',
      'Professional liability (E&O) recommended for skilled service providers',
      'Some contracts may require specific insurance minimums',
      'Consider bonding for theft protection',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS (IDAPA 16.05.06)
  // ============================================
  {
    id: 'ID-B1',
    itemId: 'B1',
    stateCode: 'ID',
    title: 'Criminal Background Check Requirements',
    description: 'Idaho requires criminal history checks for certain healthcare workers per IDAPA 16.05.06',
    severity: 'high',
    regulationCode: '16.05.06',
    regulationName: 'Background Checks',
    agencyName: 'Idaho Department of Health and Welfare',
    agencyAbbr: 'DHW',
    agencyPhone: '(208) 334-6626',
    agencyUrl: 'https://healthandwelfare.idaho.gov/providers',
    questions: [
      {
        id: 'q1',
        question: 'Do you bill Idaho Medicaid for services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
        helpText: 'Medicaid providers must comply with IDAPA 16.05.06 background check requirements.',
      },
      {
        id: 'q2',
        question: 'Do you conduct criminal background checks on all employees with client contact?',
        inputType: 'select',
        options: [
          { value: 'all_fingerprint', label: 'Yes - fingerprint-based checks for all' },
          { value: 'all_name', label: 'Yes - name-based checks for all' },
          { value: 'some', label: 'For some employees' },
          { value: 'none', label: 'No background checks' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do employees begin working with clients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per IDAPA 16.05.06, employees should not provide direct client services until background clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before clearance - this is a DIRECT VIOLATION
      { condition: 'equals', questionId: 'q3', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if no background checks at all
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
      // COMPLIANT for Medicaid provider with fingerprint checks and no early starts
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all_fingerprint' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      // COMPLIANT for private pay with proper background checks and no early starts
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'includes', questionId: 'q2', value: ['all_fingerprint', 'all_name'] },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if only some employees get checks
      { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check procedures meet Idaho requirements.',
      atRisk: 'Background checks should be conducted for all employees with client contact, not just some.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance is a direct violation. Per IDAPA 16.05.06, no client contact is permitted until clearance is received. Immediately remove uncleared staff from client assignments.',
      unknown: 'Review background check requirements per IDAPA 16.05.06.',
    },
    stateNotes: [
      'IDAPA 16.05.06 applies to Medicaid providers and certain licensed facilities',
      'Private pay agencies: Background checks strongly recommended but not state-mandated',
      'Fingerprint-based checks recommended for thorough screening',
      'Contact DHW Background Check Unit for Medicaid provider requirements',
    ],
  },

  {
    id: 'ID-B2',
    itemId: 'B2',
    stateCode: 'ID',
    title: 'Sex Offender Registry Check',
    description: 'Verify employees against the Idaho Sex Offender Registry',
    severity: 'high',
    regulationCode: '18-8301',
    regulationName: 'Sex Offender Registry',
    agencyName: 'Idaho State Police',
    agencyAbbr: 'ISP',
    agencyPhone: '(208) 884-7000',
    agencyUrl: 'https://www.isp.idaho.gov/sor_id/search.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Idaho Sex Offender Registry for all employees before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes - always before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'never', label: 'No' },
        ],
        required: true,
        helpText: 'Free registry search available at isp.idaho.gov/sor_id/search.html',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'never', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Sex offender registry checks are conducted consistently.',
      atRisk: 'Registry checks should be conducted for every employee before hire.',
      nonCompliant: 'Implement sex offender registry checks for all employees. Free search available at isp.idaho.gov.',
      unknown: 'Verify registry check procedures.',
    },
    stateNotes: [
      'Idaho Sex Offender Registry: isp.idaho.gov/sor_id/search.html',
      'Free public search available - no registration required',
      'Check by name, ZIP code, or county',
      'Registry includes sex offenders and violent offenders',
      'Maintain documentation of searches in personnel files',
    ],
  },

  {
    id: 'ID-B3',
    itemId: 'B3',
    stateCode: 'ID',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list checks required for Medicaid/Medicare billing',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '1-800-HHS-TIPS',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you bill Medicare or Medicaid?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify all employees against the OIG LEIE and SAM.gov exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes - at least monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'never', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Federal law prohibits employing excluded individuals for Medicare/Medicaid services.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'never', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Federal exclusion list verification meets requirements.',
      atRisk: 'OIG recommends monthly exclusion list checks, not just at hire.',
      nonCompliant: 'Medicare/Medicaid providers must verify employees against OIG LEIE and SAM.gov exclusion lists.',
      unknown: 'Review federal exclusion requirements.',
    },
    stateNotes: [
      'OIG LEIE: exclusions.oig.hhs.gov - free searchable database',
      'SAM.gov: sam.gov - System for Award Management exclusions',
      'Check all employees monthly who work on Medicare/Medicaid cases',
      'Employing excluded individuals = Civil Monetary Penalties up to $100,000 per item/service',
      'Document all exclusion list verifications and retain records',
    ],
  },

  // ============================================
  // SECTION C: MEDICAID PROVIDER ENROLLMENT
  // ============================================
  {
    id: 'ID-C1',
    itemId: 'C1',
    stateCode: 'ID',
    title: 'Idaho Medicaid Provider Enrollment',
    description: 'Requirements for billing Idaho Medicaid',
    severity: 'high',
    regulationCode: '16.03.09',
    regulationName: 'Medicaid Enrollment',
    agencyName: 'Idaho Medicaid - Gainwell Technologies',
    agencyAbbr: 'Gainwell',
    agencyPhone: '(866) 686-4272',
    agencyUrl: 'https://www.idmedicaid.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you want to bill Idaho Medicaid for services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you enrolled as an Idaho Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes - actively enrolled' },
          { value: 'pending', label: 'Application pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Enroll through idmedicaid.com - separate from any state licensure.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicaid provider enrollment status is appropriate for your business model.',
      atRisk: 'Medicaid application is pending. Follow up with Gainwell at (866) 686-4272.',
      nonCompliant: 'You must enroll as an Idaho Medicaid provider to bill Medicaid. Apply at idmedicaid.com.',
      unknown: 'Determine if Medicaid enrollment is needed for your services.',
    },
    stateNotes: [
      'Medicaid enrollment is separate from state licensure (which is not required)',
      'Apply through Gainwell Technologies at idmedicaid.com',
      'NPI number required for enrollment',
      'Personal care services may require specific provider type enrollment',
    ],
  },

  {
    id: 'ID-C2',
    itemId: 'C2',
    stateCode: 'ID',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid personal care services per federal 21st Century Cures Act',
    severity: 'high',
    regulationCode: '16.03.13',
    regulationName: 'EVV',
    agencyName: 'Idaho Medicaid',
    agencyAbbr: 'DHW',
    agencyPhone: '(866) 686-4272',
    agencyUrl: 'https://www.idmedicaid.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use an approved EVV system to document visits?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes - fully implemented' },
          { value: 'partial', label: 'Partially implemented' },
          { value: 'no', label: 'No EVV system' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Federal law requires EVV for Medicaid personal care services.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV requirements are met or not applicable.',
      atRisk: 'EVV must be fully implemented for all Medicaid personal care visits.',
      nonCompliant: 'Federal law requires EVV for Medicaid personal care services. Contact Idaho Medicaid for approved systems.',
      unknown: 'Determine EVV requirements for your services.',
    },
    stateNotes: [
      '21st Century Cures Act requires EVV for Medicaid personal care services',
      'Idaho has approved EVV systems - check with Idaho Medicaid for current list',
      'EVV must capture: visit location, date/time, services provided, worker identity',
      'Non-compliance may result in Medicaid reimbursement reductions',
      'Contact Gainwell Technologies at (866) 686-4272 for EVV assistance',
    ],
  },

  // ============================================
  // SECTION D: MEDICARE CERTIFICATION (HHAs ONLY)
  // ============================================
  {
    id: 'ID-D1',
    itemId: 'D1',
    stateCode: 'ID',
    title: 'Medicare Certification (Skilled Services Only)',
    description: 'Medicare certification required to bill Medicare for skilled home health services',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare Certification',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(208) 334-6626',
    agencyUrl: 'https://www.cms.gov/Medicare/Provider-Enrollment-and-Certification/CertificationandComplianc/HHAs',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide skilled services (nursing, therapy) and want to bill Medicare?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - non-medical services only' },
        ],
        required: true,
        helpText: 'Medicare certification is only needed for skilled home health services. Non-medical personal care does not require Medicare certification.',
      },
      {
        id: 'q2',
        question: 'Are you Medicare certified?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes - CMS certified' },
          { value: 'pending', label: 'Certification in progress' },
          { value: 'not_certified', label: 'Not certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification status is appropriate for your services.',
      atRisk: 'Medicare certification is in progress. Contact Idaho Bureau of Facility Standards at (208) 334-6626 for status.',
      nonCompliant: 'Medicare certification required to bill Medicare for skilled services. Note: Idaho no longer requires separate state licensure as of July 2025.',
      unknown: 'Determine if Medicare certification is needed for your services.',
    },
    stateNotes: [
      'IMPORTANT: Idaho eliminated state licensure for Medicare-certified HHAs effective July 1, 2025',
      'Only federal CMS certification is required - no separate state license',
      'Contact Bureau of Facility Standards at (208) 334-6626 for initial certification',
      'Non-medical agencies do NOT need Medicare certification',
    ],
  },

  // ============================================
  // SECTION E: BEST PRACTICES (RECOMMENDED)
  // ============================================
  {
    id: 'ID-E1',
    itemId: 'E1',
    stateCode: 'ID',
    title: 'Employee Training (Best Practice)',
    description: 'Training recommendations for Idaho home care agencies',
    severity: 'low',
    regulationCode: 'Best Practice',
    regulationName: 'Training',
    agencyName: 'N/A - Industry Best Practice',
    agencyAbbr: 'N/A',
    agencyPhone: '',
    agencyUrl: '',
    questions: [
      {
        id: 'q1',
        question: 'Although not state-mandated for non-medical agencies, do you provide training to caregivers?',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Initial orientation before client contact' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'infection', label: 'Infection control/universal precautions' },
          { value: 'abuse', label: 'Abuse recognition and reporting' },
          { value: 'hipaa', label: 'Privacy/confidentiality (HIPAA awareness)' },
          { value: 'cpr', label: 'CPR/First Aid certification' },
          { value: 'ongoing', label: 'Ongoing/annual training' },
        ],
        required: true,
        helpText: 'While Idaho does not mandate specific training for non-medical agencies, training improves quality and reduces liability.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your training program follows industry best practices.',
      atRisk: 'Consider expanding your training program to cover additional topics.',
      nonCompliant: 'While not state-mandated, comprehensive training protects clients and reduces your liability.',
      unknown: 'Review training practices against industry standards.',
    },
    stateNotes: [
      'Idaho does NOT mandate specific training hours for non-medical personal care agencies',
      'Medicare-certified HHAs must follow federal CMS training requirements (42 CFR 484)',
      'Training is still strongly recommended as a business best practice',
      'Consider industry certifications for competitive advantage',
    ],
  },

  {
    id: 'ID-E2',
    itemId: 'E2',
    stateCode: 'ID',
    title: 'Service Agreements (Best Practice)',
    description: 'Written service agreements protect both agency and clients',
    severity: 'low',
    regulationCode: 'Best Practice',
    regulationName: 'Service Agreements',
    agencyName: 'N/A - Industry Best Practice',
    agencyAbbr: 'N/A',
    agencyPhone: '',
    agencyUrl: '',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written service agreements with all clients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your service agreements include:',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Description of services to be provided' },
          { value: 'rates', label: 'Rates and payment terms' },
          { value: 'schedule', label: 'Service schedule' },
          { value: 'cancellation', label: 'Cancellation policy' },
          { value: 'liability', label: 'Liability limitations' },
          { value: 'termination', label: 'Termination procedures' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'AT_RISK' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Service agreements are comprehensive.',
      atRisk: 'Consider strengthening your service agreements to include additional protections.',
      nonCompliant: 'Written service agreements are strongly recommended to protect your business and clients.',
      unknown: 'Review service agreement practices.',
    },
    stateNotes: [
      'Written service agreements are recommended but not state-mandated for non-medical agencies',
      'Include clear scope of services, rates, payment terms, and cancellation policy',
      'Have agreements reviewed by an attorney for Idaho contract law compliance',
      'Keep signed copies in client files',
      'Consider arbitration clauses for dispute resolution',
    ],
  },

  {
    id: 'ID-E3',
    itemId: 'E3',
    stateCode: 'ID',
    title: 'Record Keeping (Best Practice)',
    description: 'Recommended record keeping practices for Idaho home care agencies',
    severity: 'low',
    regulationCode: 'Best Practice',
    regulationName: 'Records',
    agencyName: 'N/A - Industry Best Practice',
    agencyAbbr: 'N/A',
    agencyPhone: '',
    agencyUrl: '',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'none', label: 'No formal retention policy' },
        ],
        required: true,
        helpText: 'While Idaho has no state mandate for non-medical agencies, 7-year retention aligns with federal and legal best practices.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'AT_RISK' },
      { condition: 'includes', value: ['less5', 'none'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention practices meet industry standards.',
      atRisk: 'Consider extending record retention to 7 years for legal protection.',
      nonCompliant: 'Establish a formal record retention policy. 7-year retention is recommended.',
      unknown: 'Review record retention practices.',
    },
    stateNotes: [
      'Idaho does not mandate specific record retention for non-medical agencies',
      'Medicare-certified HHAs: 5 years minimum per 42 CFR 484.110',
      'Industry best practice: 7 years for all records',
      'Retain records for minors until 2 years after they reach age 18',
      'Electronic records acceptable if properly backed up and accessible',
    ],
  },

  // ============================================
  // SECTION F: MANDATORY ABUSE REPORTING
  // ============================================
  {
    id: 'ID-F1',
    itemId: 'F1',
    stateCode: 'ID',
    title: 'Mandatory Abuse Reporting (Idaho Code 39-5303)',
    description: 'Home care workers are mandatory reporters of suspected abuse, neglect, or exploitation of vulnerable adults under Idaho law',
    severity: 'high',
    regulationCode: '39-5303',
    regulationName: 'Abuse Reporting',
    agencyName: 'Idaho Commission on Aging',
    agencyAbbr: 'ICOA',
    agencyPhone: '(877) 471-2777',
    agencyUrl: 'https://aging.idaho.gov/stay-safe/online-reporting-tools/',
    questions: [
      {
        id: 'q1',
        question: 'Are all staff trained on mandatory abuse reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'some', label: 'Some staff are trained' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Home care workers are mandatory reporters under Idaho Code 39-5303.',
      },
      {
        id: 'q2',
        question: 'Do staff know the Idaho Adult Protection Services hotline number?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - (877) 471-2777 is posted and known' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do staff understand the 4-hour reporting requirement for serious cases?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'When abuse results in death or serious physical injury, reports must be made within 4 hours.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Mandatory abuse reporting training meets Idaho requirements under Idaho Code 39-5303.',
      atRisk: 'Not all staff are trained on mandatory reporting. Train all staff immediately - home care workers are mandatory reporters.',
      nonCompliant: 'Staff are not trained on mandatory reporting requirements. Failure to report is a misdemeanor. Idaho APS Hotline: (877) 471-2777.',
      unknown: 'Verify mandatory reporting training is in place.',
    },
    stateNotes: [
      'Idaho APS Hotline: (877) 471-2777 or (208) 334-3833',
      'Home care workers are mandatory reporters under Idaho Code 39-5303',
      'Report within 4 hours for serious cases (death or serious physical injury)',
      'Failure to report is a misdemeanor (up to 6 months imprisonment or $1,000 fine)',
      'Online reporting available at aging.idaho.gov for mandated reporters',
      'Reports can be made anonymously but this makes investigation more difficult',
    ],
  },
];

export default IDAHO_QUESTIONS;